/**
 * Read More / Read Less custom element
 * Truncates content after the first two paragraphs and allows expanding/collapsing
 */
class ReadMore extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.toggleButton = null;
    this.isExpanded = false;
    this.firstParagraph = null;
    this.remainingContent = null;
  }

  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    if (!this.content) return;

    const allElements = this.content.querySelectorAll(
      ':scope > p, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6'
    );

    // Filter out empty paragraphs (only whitespace or only <br>) and remove them from DOM
    const paragraphs = [];
    for (const el of allElements) {
      const text = el.textContent.trim();
      const html = el.innerHTML.trim();
      if (text.length > 0 && html !== '<br>') {
        paragraphs.push(el);
      } else {
        el.remove();
      }
    }

    if (paragraphs.length <= 2) return;

    this.#setupContent(paragraphs);
    this.#createToggleButton();
    this.#bindEvents();
  }

  #setupContent(elements) {
    // Keep the first 2 elements visible, hide the rest
    const remainingElements = elements.slice(2);

    if (remainingElements.length === 0) return;

    // Create wrapper for remaining content
    this.remainingContent = document.createElement('div');
    this.remainingContent.className = 'read-more__hidden';
    this.remainingContent.setAttribute('data-read-more-hidden', '');

    for (const el of remainingElements) {
      this.remainingContent.appendChild(el.cloneNode(true));
      el.remove();
    }

    this.content.appendChild(this.remainingContent);
  }

  #createToggleButton() {
    if (!this.remainingContent) return;

    const readMoreLabel = this.dataset.readMoreLabel || 'Lire plus';
    const readLessLabel = this.dataset.readLessLabel || 'Réduire';

    this.toggleButton = document.createElement('button');
    this.toggleButton.type = 'button';
    this.toggleButton.className = 'read-more__toggle';
    this.toggleButton.setAttribute('data-read-more-toggle', '');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.toggleButton.innerHTML = `
      <span class="read-more__label" data-more>${readMoreLabel}</span>
      <span class="read-more__label" data-less hidden>${readLessLabel}</span>
      <span class="read-more__icon">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;

    this.content.appendChild(this.toggleButton);
  }

  #bindEvents() {
    if (!this.toggleButton) return;
    this.toggleButton.addEventListener('click', () => this.#toggle());
  }

  #toggle() {
    this.isExpanded = !this.isExpanded;

    this.remainingContent.classList.toggle('read-more__hidden', !this.isExpanded);
    this.remainingContent.classList.toggle('read-more__visible', this.isExpanded);

    this.toggleButton.setAttribute('aria-expanded', this.isExpanded.toString());
    this.toggleButton.classList.toggle('read-more__toggle--expanded', this.isExpanded);

    const moreLabel = this.toggleButton.querySelector('[data-more]');
    const lessLabel = this.toggleButton.querySelector('[data-less]');

    moreLabel.hidden = this.isExpanded;
    lessLabel.hidden = !this.isExpanded;
  }
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}
